import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { environment } from 'src/environments/environment'
import {
  H2HPet,
  H2HPetAdoptOrFoster,
  H2HPetApproval,
  H2HPetGender,
  TriBool,
} from '../types/pet'
import { getApiValueFromDisplay } from '../constants/can_live_with'
import { ReviewService } from './review.service'
import { FormPhoto, PhotoService } from './photo.service'
import { H2HReport } from '../types/report'
import { H2H_PET_STATUS, H2H_PET_STATUS_LABELS } from '../constants/pet'
import { AuthService } from './auth.service'
import {
  REJECTION_REASONS,
  REMOVAL_REASONS,
} from '../constants/removal_reasons'
import { map, Observable } from 'rxjs'
import { ungzip } from 'pako'
import { SPECIES_LIST } from '../constants/species'
import { Router } from '@angular/router'

@Injectable({
  providedIn: 'root',
})
export class PetService {
  private API_BASE_URL = environment.apiBaseUrl
  private http = inject(HttpClient)
  private reviewService = inject(ReviewService)
  private photoService = inject(PhotoService)
  private authService = inject(AuthService)
  private router = inject(Router)

  private transformSearchBodyForV2(
    petSearchBody: PetSearchBody,
  ): PetSearchBody {
    const transformed = { ...petSearchBody }

    // Handle adoptOrFoster specially - if both are selected, omit the parameter
    if (Array.isArray(transformed.adoptOrFoster)) {
      if (
        transformed.adoptOrFoster.length === 2 &&
        transformed.adoptOrFoster.includes('ADOPT') &&
        transformed.adoptOrFoster.includes('FOSTER')
      ) {
        // Both selected - omit parameter to show all pets
        delete transformed.adoptOrFoster
      } else if (transformed.adoptOrFoster.length === 1) {
        // Single selection - use the single value
        transformed.adoptOrFoster = transformed.adoptOrFoster[0]
      }
    }

    // Convert arrays to single values for v2 API (excluding adoptOrFoster which is handled above)
    const arrayToSingleFields = ['species', 'gender', 'size', 'spayed', 'breed']

    arrayToSingleFields.forEach((field) => {
      const value = (transformed as any)[field]
      if (Array.isArray(value) && value.length > 0) {
        // Take the first value from array for v2 API
        ;(transformed as any)[field] = value[0]
      }
    })

    // Handle verified field - convert YES/NO to boolean
    if (
      Array.isArray(transformed.verified) &&
      transformed.verified.length > 0
    ) {
      const verifiedValue = transformed.verified[0]
      if (verifiedValue === 'YES') {
        transformed.verified = true
      } else if (verifiedValue === 'NO') {
        transformed.verified = false
      } else {
        delete transformed.verified
      }
    }

    // Ensure zipcode is a number for v1 or certain v2 cases, but allow string for others
    if (transformed.zipcode && typeof transformed.zipcode === 'string') {
      // No longer parsing to int here to support Canadian zip codes
      // The backend will handle validation.
    }

    // Ensure distance is a string or number
    if (transformed.distance && Array.isArray(transformed.distance)) {
      transformed.distance = transformed.distance[0]
    }

    // Handle age field - convert array of ranges to proper format
    if (Array.isArray(transformed.age) && transformed.age.length > 0) {
      if (transformed.age.length === 1) {
        // Single age range like ['0-1'] -> convert to {gte: 0, lte: 1}
        const ageRange = transformed.age[0]
        if (typeof ageRange === 'string' && ageRange.includes('-')) {
          const [gte, lte] = ageRange.split('-').map((n) => parseInt(n, 10))
          if (!isNaN(gte) && !isNaN(lte)) {
            transformed.age = { gte, lte }
          }
        }
      } else {
        // Multiple age ranges - the backend expects this as an array of ranges
        // Keep as array for now, backend will handle multiple ranges
        transformed.age = transformed.age
      }
    }

    // Handle canLiveWith field - transform UI display values to API values
    if (
      Array.isArray(transformed.canLiveWith) &&
      transformed.canLiveWith.length > 0
    ) {
      transformed.canLiveWith = transformed.canLiveWith.map(
        (displayValue: string) => {
          // Ensure proper capitalization (e.g., "cats" -> "Cats")
          const properlyCapitalized =
            displayValue.charAt(0).toUpperCase() +
            displayValue.slice(1).toLowerCase()
          return getApiValueFromDisplay(properlyCapitalized)
        },
      )
    }

    console.log('PetService - Transformed search body for v2:', transformed)
    return transformed
  }

  searchPets(
    petSearchBody: PetSearchBody,
    queryParams?: PetSearchQueryParams,
    isWordPress: boolean = false,
    useV2Api: boolean = true,
  ): Observable<H2HPet[] | { filtered_pets: H2HPet[]; extra_pets: H2HPet[] }> {
    // Log search parameters for debugging
    console.log('PetService.searchPets - Search body:', petSearchBody)
    console.log('PetService.searchPets - Query params:', queryParams)

    // Construct the query parameters
    let params = new HttpParams()
    // Add query parameters to the params object
    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        params = params.append(key, value)
      }
    }

    // For WordPress requests, always use v1 endpoint
    if (isWordPress) {
      console.log('PetService - Using v1 endpoint (WordPress request)')
      const endpoint = `${this.API_BASE_URL}/pet/search`
      return this.http.post<H2HPet[]>(endpoint, petSearchBody, { params })
    }

    // For non-WordPress routes, use normal endpoint logic
    const endpoint = useV2Api
      ? `${this.API_BASE_URL}/v2/pet/search`
      : `${this.API_BASE_URL}/pet/search`

    // Transform search body for v2 API
    let transformedSearchBody = petSearchBody
    if (useV2Api) {
      transformedSearchBody = this.transformSearchBodyForV2(petSearchBody)
    }

    console.log(`PetService - Final endpoint decision: ${endpoint}`)
    console.log('PetService - Search body being sent:', transformedSearchBody)
    console.log('PetService - Query params being sent:', params)

    return this.http.post<
      H2HPet[] | { filtered_pets: H2HPet[]; extra_pets: H2HPet[] }
    >(endpoint, transformedSearchBody, { params })
  }

  searchPetsPaginated(
    petSearchBody: PetSearchBody,
    queryParams?: PetSearchQueryParams,
    lastEvaluatedToken?: string,
    isWordPress: boolean = false,
    useV2Api: boolean = true,
  ): Observable<
    HttpResponse<H2HPet[] | { filtered_pets: H2HPet[]; extra_pets: H2HPet[] }>
  > {
    // Log search parameters for debugging
    console.log('PetService.searchPetsPaginated - Search body:', petSearchBody)
    console.log('PetService.searchPetsPaginated - Query params:', queryParams)
    console.log(
      'PetService.searchPetsPaginated - Last token:',
      lastEvaluatedToken,
    )

    let headers = new HttpHeaders()
    let params = new HttpParams()

    // Handle pagination token (could be traditional token or v2 offset-based)
    if (lastEvaluatedToken) {
      try {
        const tokenData = JSON.parse(lastEvaluatedToken)

        // Check if this is v2 offset-based pagination
        if (tokenData.v2_offset_pagination) {
          console.log(
            'PetService - Using v2 offset-based pagination:',
            tokenData,
          )
          // For v2 offset pagination, pass offset as query parameter
          params = params.append('offset', tokenData.offset.toString())
        } else {
          // Traditional token-based pagination
          headers = headers.append('pagination-token', lastEvaluatedToken)
        }
      } catch (e) {
        // If parsing fails, treat as traditional token
        console.log('PetService - Using traditional pagination token')
        headers = headers.append('pagination-token', lastEvaluatedToken)
      }
    }

    // Add query parameters to the params object
    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        params = params.append(key, value)
      }
    }

    // For WordPress requests, always use v1 endpoint
    if (isWordPress) {
      console.log('PetService - Using v1 endpoint (WordPress request)')
      const endpoint = `${this.API_BASE_URL}/pet/search`
      return this.http.post<H2HPet[]>(endpoint, petSearchBody, {
        params,
        headers,
        observe: 'response',
      })
    }

    // For non-WordPress routes, use normal endpoint logic
    const endpoint = useV2Api
      ? `${this.API_BASE_URL}/v2/pet/search`
      : `${this.API_BASE_URL}/pet/search`

    // Transform search body for v2 API
    let transformedSearchBody = petSearchBody
    if (useV2Api) {
      transformedSearchBody = this.transformSearchBodyForV2(petSearchBody)
    }

    console.log(`PetService - Final paginated endpoint decision: ${endpoint}`)
    console.log(
      'PetService - Paginated search body being sent:',
      transformedSearchBody,
    )
    console.log('PetService - Paginated query params being sent:', params)

    return this.http.post<
      H2HPet[] | { filtered_pets: H2HPet[]; extra_pets: H2HPet[] }
    >(endpoint, transformedSearchBody, {
      params,
      headers,
      observe: 'response',
    })
  }

  searchPetsAdmin(
    petSearchBody: PetSearchBody,
    queryParams?: PetSearchQueryParams,
    lastEvaluatedToken?: string,
  ): Observable<HttpResponse<H2HPet[]>> {
    console.log('PetService.searchPetsAdmin - Search body:', petSearchBody)
    console.log('PetService.searchPetsAdmin - Query params:', queryParams)

    let headers = new HttpHeaders()
    let params = new HttpParams()

    if (lastEvaluatedToken) {
      headers = headers.append('pagination-token', lastEvaluatedToken)
    }

    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        params = params.append(key, value)
      }
    }

    const endpoint = `${this.API_BASE_URL}/v2/pet/search/admin`

    console.log('PetService - Using admin endpoint:', endpoint)

    return this.http.post<H2HPet[]>(endpoint, petSearchBody, {
      params,
      headers,
      observe: 'response',
    })
  }

  getMyPets(queryParams?: PetSearchQueryParams) {
    // Construct the query parameters
    let params = new HttpParams()
    // Add query parameters to the params object
    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        params = params.append(key, value)
      }
    }

    return this.http.get<H2HPet[]>(`${this.API_BASE_URL}/pet/my`, { params })
  }
  getAdopted(queryParams?: PetSearchQueryParams) {
    // Construct the query parameters
    let params = new HttpParams()
    // Add query parameters to the params object
    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        params = params.append(key, value)
      }
    }

    return this.http.get<H2HPet[]>(`${this.API_BASE_URL}/pet/adopted`, {
      params,
    })
  }
  getInContract(queryParams?: PetSearchQueryParams) {
    // Construct the query parameters
    let params = new HttpParams()
    // Add query parameters to the params object
    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        params = params.append(key, value)
      }
    }

    return this.http.get<H2HPet[]>(`${this.API_BASE_URL}/pet/in-contract`, {
      params,
    })
  }

  getExpiredPets(queryParams?: PetSearchQueryParams) {
    const userUuid = this.authService.getUuid()
    if (!userUuid) {
      throw new Error('User not authenticated')
    }

    return this.searchPets(
      {
        creatorUuid: userUuid,
        status: 'EXPIRED',
      },
      { ...queryParams, includeExpired: true },
    )
  }

  getPet(id: string) {
    // V2 endpoint - optimized with timeout protection and efficient user lookups
    return this.http.get<H2HPet>(`${this.API_BASE_URL}/v2/pet/${id}`)
  }

  addPet(pet: H2HPet, publishToFacebook: boolean = false) {
    try {
      // Create a deep copy to avoid modifying the original
      const petToSend = JSON.parse(JSON.stringify(pet))

      // Ensure address object exists
      if (!petToSend.address) {
        petToSend.address = {}
      }

      // Set country based on zipcode format if not already specified
      if (!petToSend.address.country && petToSend.address.zipcode) {
        const zipcode = String(petToSend.address.zipcode).trim()

        // Canadian postal codes have letters
        const hasLetters = /[A-Za-z]/.test(zipcode)

        if (hasLetters) {
          petToSend.address.country = 'Canada'
        } else {
          petToSend.address.country = 'United States'
        }
      } else if (!petToSend.address.country) {
        petToSend.address.country = 'United States'
      }

      // Add publishToFacebook flag if provided
      if (publishToFacebook) {
        petToSend.publishToFacebook = publishToFacebook
      }

      return this.http.post<H2HPet>(`${this.API_BASE_URL}/v2/pet`, petToSend)
    } catch (error) {
      throw error
    }
  }

  updatePet(petFields: Partial<H2HPet>, id: string) {
    return this.http.put(`${this.API_BASE_URL}/v2/pet/${id}`, petFields)
  }

  deletePet(id: string, removalReason: string) {
    return this.http.delete(`${this.API_BASE_URL}/pet/${id}`, {
      body: { comments: removalReason },
    })
  }

  repostPet(id: string) {
    return this.http.post<H2HPet>(`${this.API_BASE_URL}/v2/pet/status/${id}`, {
      action: 'REPOST',
    })
  }

  getFavoritePets() {
    return this.http.get<H2HPet[]>(`${this.API_BASE_URL}/pet/favorite`)
  }

  favoritePet(petId: string) {
    return this.reviewService.createReview({
      objId: petId,
      objType: 'PET',
      reviewType: 'FAVORITE',
    })
  }

  unfavoritePet(reviewId: string) {
    return this.reviewService.deleteReview(reviewId)
  }

  getHiddenPets() {
    return this.http.get<H2HPet[]>(`${this.API_BASE_URL}/pet/hidden`)
  }

  hidePet(petId: string) {
    return this.reviewService.createReview({
      objId: petId,
      objType: 'PET',
      reviewType: 'HIDE',
    })
  }

  unhidePet(reviewId: string) {
    return this.reviewService.deleteReview(reviewId)
  }

  createPetPhoto(files: Blob[], objId: string, avatar?: Blob) {
    const formPhotos: FormPhoto[] = files.map((file, i) => [`file${i}`, file])
    // will automatically set the avatar of the pet in the backend
    if (avatar) {
      formPhotos.push(['setAvatar', avatar])
    }

    return this.photoService.createPhoto(formPhotos, 'PET', objId)
  }

  assignPetToShelter(
    petUuid: string,
    shelterUuid: string,
    action: 'ASSIGN' | 'UNASSIGN',
  ) {
    return this.http.post<H2HPet>(
      `${this.API_BASE_URL}/pet/${petUuid}/shelter`,
      {
        shelterUuid,
        action,
      },
    )
  }

  refreshPet(petUuid: string, expiresAt?: number, comments?: string) {
    return this.http.post<H2HPet>(
      `${this.API_BASE_URL}/v2/pet/status/${petUuid}`,
      {
        action: 'REFRESH',
        expiresAt,
        comments: comments,
      },
    )
  }

  changePetShelterStatus(
    petUuid: string,
    action:
      | 'APPROVED'
      | 'REJECTED'
      | 'IN_REVIEW'
      | 'PENDING'
      | 'EXPIRED'
      | 'ADOPTED'
      | 'FOSTERED',
    comments?: string,
  ) {
    return this.http.post<H2HPet>(
      `${this.API_BASE_URL}/v2/pet/status/${petUuid}`,
      {
        action,
        comments: comments,
      },
    )
  }

  convertTimestampToDate(timestamp: number): string {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    })
  }

  // Pet Reports
  getPastPetActions(shelterUuid: string, startTime?: number, endTime?: number) {
    const now = new Date().getTime()
    const start = startTime || now - 60 * 60 * 24 * 3 * 1000
    const end = endTime || now
    return this.http.get<H2HPetApproval[]>(
      `${this.API_BASE_URL}/pet/actions/${shelterUuid}?startTime=${start}&endTime=${end}`,
    )
  }
  getHistoricPets(
    searchBody: PetSearchBody,
    shelterUuid: string,
    startTime?: number,
    endTime?: number,
  ) {
    const now = new Date().getTime()
    const start = startTime || now - 60 * 60 * 24 * 3 * 1000
    const end = endTime || now
    return this.http.post<H2HPet[]>(
      `${this.API_BASE_URL}/pet/history/${shelterUuid}?startTime=${start}&endTime=${end}`,
      searchBody,
    )
  }
  /**
   * Get reports using the v1 endpoint - specifically for shelter admin and super admin reporting
   * This method is intentionally kept on v1 to ensure reporting functionality remains stable
   * and uses the v1 search logic for consistent results.
   */
  getReportsV1(
    shelterUuid: string,
    filters: any,
    startTime?: number,
    endTime?: number,
    approvedStartTime?: number,
    approvedEndTime?: number,
    outcomeStartTime?: number,
    outcomeEndTime?: number,
  ) {
    const now = new Date().getTime()
    const start = startTime || now - 60 * 60 * 24 * 3 * 1000
    const end = endTime || now

    // Explicitly use v1 endpoint for reporting
    let url = `${this.API_BASE_URL}/pet/report`

    if (shelterUuid) {
      url += `/${shelterUuid}`
    }

    const params = {
      startTime: start,
      endTime: end,
    } as any

    if (approvedStartTime) {
      params.approvedStartTime = approvedStartTime
    }
    if (approvedEndTime) {
      params.approvedEndTime = approvedEndTime
    }

    if (outcomeStartTime) {
      params.outcomeStartTime = outcomeStartTime
    }
    if (outcomeEndTime) {
      params.outcomeEndTime = outcomeEndTime
    }

    return this.http
      .post<string>(`${url}`, filters, {
        params,
        headers: {
          'accept-enconding': 'gzip, deflate, br',
        },
      })
      .pipe(
        map((resp) => {
          try {
            const gezipedData = atob(resp)
            const gzipedDataArray = Uint8Array.from(gezipedData, (c) =>
              c.charCodeAt(0),
            )

            const ungzipedData = ungzip(gzipedDataArray)

            const decoded = new TextDecoder().decode(ungzipedData)

            return JSON.parse(decoded) as H2HReportResp
          } catch (e) {
            return typeof resp === 'string' ? JSON.parse(resp) : resp
          }
        }),
      )
  }

  /**
   * @deprecated Use getReportsV1 instead for reporting functionality
   * This method is kept for backward compatibility but will be removed in future versions
   */
  getReports(
    shelterUuid: string,
    filters: any,
    startTime?: number,
    endTime?: number,
    approvedStartTime?: number,
    approvedEndTime?: number,
    outcomeStartTime?: number,
    outcomeEndTime?: number,
  ) {
    return this.getReportsV1(
      shelterUuid,
      filters,
      startTime,
      endTime,
      approvedStartTime,
      approvedEndTime,
      outcomeStartTime,
      outcomeEndTime,
    )
  }

  getStatusLabels() {
    const filteredLabels = { ...H2H_PET_STATUS_LABELS } as Partial<
      typeof H2H_PET_STATUS_LABELS
    >

    // only super admins can filter the REJECTED status
    if (!this.authService.isSuperAdmin) {
      delete filteredLabels.REJECTED
      delete filteredLabels.SENT_TO_HQ
    }

    return filteredLabels
  }

  formatPetStatus(status: H2H_PET_STATUS) {
    return H2H_PET_STATUS_LABELS[status]
  }

  formatRejectionReason(reason: keyof typeof REJECTION_REASONS) {
    return REJECTION_REASONS[reason]
  }

  formatRemovalReason(reason: keyof typeof REMOVAL_REASONS) {
    return REMOVAL_REASONS[reason]
  }

  getTriboolIconName(value: TriBool) {
    switch (value) {
      case 'YES':
        return 'checkmark-outline'
      case 'NO':
        return 'close-outline'
      case 'NA':
        return 'help-outline'
      default:
        return 'help-outline'
    }
  }

  getShareLink(petId: string): string {
    // Return the SharePet v2 API endpoint for social media sharing
    // This endpoint serves HTML with Open Graph meta tags and redirects browsers to the frontend
    return `${this.API_BASE_URL}/v2/pet/${petId}/share`
  }

  /**
   * Calls the boost payment API to update the pet's boosted status
   */
  boostPet(
    petUuid: string,
    boostAmount?: number,
    instagramBoost?: boolean,
    userData?: any,
    packageInfo?: string,
    paymentIntentId?: string,
    clientIP?: string,
  ) {
    const body = {
      petUuid,
      boostAmount: boostAmount || 0, // Amount in cents
      instagramBoost: instagramBoost || false, // Whether Instagram boost was purchased
      userData: userData || {}, // User information from auth service
      packageInfo: packageInfo || 'Standard Boost', // Package details
      paymentIntentId: paymentIntentId || '', // Stripe payment intent ID
      clientIP: clientIP || 'Unknown', // Client IP captured on frontend
    }
    console.log('PetService.boostPet called with:', {
      petUuid,
      boostAmount,
      instagramBoost,
      userData: userData
        ? `${userData.firstName} ${userData.lastName} (${userData.email})`
        : 'No user data',
      packageInfo,
      body,
    })
    console.log('API URL:', `${this.API_BASE_URL}/social-media/boost/payment`)

    return this.http.post<{
      message: string
      petUuid: string
      updatedAttributes: any
    }>(`${this.API_BASE_URL}/social-media/boost/payment`, body)
  }
}

export type PetSearchQueryParams = {
  includeExpired?: boolean
  includeHidden?: boolean
  includeConversation?: boolean
  includeOutcome?: boolean
  includeContract?: boolean
  ignoreContractStatus?: boolean
  tableView?: boolean
  byMe?: boolean
  limit?: number
}

export type PetSearchBody = {
  name?: string
  species?: (typeof SPECIES_LIST)[number] | (typeof SPECIES_LIST)[number][]
  zipcode?: number | string
  distance?: string | number
  gender?: H2HPetGender | H2HPetGender[]
  age?:
    | {
        lte?: number
        gte?: number
      }
    | string[]
  adoptOrFoster?: H2HPetAdoptOrFoster | H2HPetAdoptOrFoster[]
  shelterUuid?: string
  creatorUuid?: string
  status?: H2H_PET_STATUS | 'ALL' | H2H_PET_STATUS[]
  size?: 'SM' | 'MD' | 'LG' | ('SM' | 'MD' | 'LG')[]
  spayed?: TriBool | TriBool[]
  // V2 API additional fields
  sortBy?:
    | 'distance'
    | 'createdAt'
    | 'updatedAt'
    | 'age'
    | 'name'
    | 'breed'
    | 'size'
    | 'approvedAt'
  sortOrder?: 'asc' | 'desc'
  canLiveWith?: string[]
  verified?: boolean | string[]
  breed?: string | string[]
  approvedAt?: {
    gte?: number
    lte?: number
  }
}

export type H2HReportResp = {
  report: H2HReport
  pets: H2HPet[]
  actions: H2HPetApproval[]
  transfers: H2HPetApproval[]
  timeFrame: [Number, Number]
}
